import { useEffect, useRef, useState } from 'react';
import { Bell, ExternalLink } from 'lucide-react';
import { useAlertasLicitacoes, useMarcarAlertaLido } from '@/hooks/useBuscasSalvas';

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString('pt-BR');

function AlertasBell() {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const { data } = useAlertasLicitacoes();
  const marcarLidoMutation = useMarcarAlertaLido();

  const alertas = data?.alertas || [];
  const totalNaoLidos = data?.total_nao_lidos || 0;

  useEffect(() => {
    if (!open) return;

    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        aria-label="Alertas de licitações"
        onClick={() => setOpen((p) => !p)}
        className="relative p-2 rounded-lg text-ds-text-secondary hover:bg-ds-surface2 hover:text-ds-text-primary transition-colors"
      >
        <Bell className="h-5 w-5" />
        {totalNaoLidos > 0 && (
          <span className="absolute top-0.5 right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-ds-danger text-white text-[10px] leading-4 text-center font-medium">
            {totalNaoLidos > 9 ? '9+' : totalNaoLidos}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto rounded-xl border border-ds-border bg-ds-surface shadow-2xl z-50">
          <div className="px-4 py-3 border-b border-ds-border">
            <h4 className="text-sm font-semibold text-ds-text-primary">Alertas de licitações</h4>
          </div>

          {alertas.length === 0 && (
            <p className="px-4 py-6 text-sm text-ds-text-secondary text-center">
              Nenhum alerta novo.
            </p>
          )}

          <ul className="divide-y divide-ds-border">
            {alertas.map((alerta) => (
              <li key={alerta.id} className="px-4 py-3 hover:bg-ds-surface2 transition-colors">
                <p className="text-xs text-ds-text-secondary mb-1">
                  Busca: <span className="font-medium text-ds-text-primary">{alerta.busca_nome}</span>
                  {' · '}{formatDate(alerta.created_at)}
                </p>
                {alerta.score !== null && alerta.score !== undefined ? (
                  <p className="text-sm text-ds-text-primary mb-2 line-clamp-2">
                    <span className="font-semibold text-ds-success">{alerta.score}% de aderência</span>
                    {' — '}{alerta.objeto}
                  </p>
                ) : (
                  <p className="text-sm text-ds-text-primary mb-2 line-clamp-2">{alerta.objeto}</p>
                )}
                <div className="flex items-center justify-between gap-2">
                  {alerta.link ? (
                    <a
                      href={alerta.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-ds-success hover:underline"
                    >
                      <ExternalLink className="h-3 w-3" />
                      Abrir
                    </a>
                  ) : <span />}
                  <button
                    type="button"
                    onClick={() => marcarLidoMutation.mutate(alerta.id)}
                    className="text-xs text-ds-text-secondary hover:text-ds-text-primary underline"
                  >
                    Marcar como lido
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export { AlertasBell };
